import { Modal, Form, Input, Space, Button } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { AnnotationColorBtn } from "../button";
import styled from "styled-components";

const { TextArea } = Input;

interface AnnotationInfoPanelProps {
  selectedAnnotation: any;
  annotationInfoOpen: boolean;
  infoPanelPosition: { x: number; y: number };
  showEditModal: boolean;
  annotationForm: any;
  requiresEditPassword?: boolean;
  passwordModalOpen?: boolean;
  passwordModalAction?: 'delete' | null;
  passwordInput?: string;
  getCurrentColor: () => string;
  getShapeType: (annotation: any) => string;
  changeColor: (color: string) => void;
  handleEditAnnotation: () => void;
  handleDeleteAnnotation: () => void;
  submitEditAnnotation: (values: any) => void;
  closeAnnotationInfo: () => void;
  setShowEditModal: (open: boolean) => void;
  setPasswordModalOpen?: (open: boolean) => void;
  setPasswordInput?: (value: string) => void;
  handlePasswordConfirm?: () => void;
  isReadOnly?: boolean;
}

export const AnnotationInfoPanel: React.FC<AnnotationInfoPanelProps> = ({
  selectedAnnotation,
  annotationInfoOpen,
  infoPanelPosition,
  showEditModal,
  annotationForm,
  requiresEditPassword = false,
  passwordModalOpen = false,
  passwordModalAction = null,
  passwordInput = "",
  getCurrentColor,
  getShapeType,
  changeColor,
  handleEditAnnotation,
  handleDeleteAnnotation,
  submitEditAnnotation,
  closeAnnotationInfo,
  setShowEditModal,
  setPasswordModalOpen,
  setPasswordInput,
  handlePasswordConfirm,
  isReadOnly = false,
}) => {
const onEditClick = (e: React.MouseEvent) => {
  e.stopPropagation(); // 부모(OSD)로 클릭 이벤트가 올라가는 것을 방지
  handleEditAnnotation();
};

const onDeleteClick = (e: React.MouseEvent) => {
  e.stopPropagation(); // 삭제 버튼도 마찬가지로 전파 방지
  handleDeleteAnnotation();
};

const onCloseClick = (e: React.MouseEvent) => {
  e.stopPropagation();
  closeAnnotationInfo();
};

// 모달 내부 클릭이 OSD로 전파되는 것도 방지 (혹시 모를 사이드 이펙트 차단)
const onModalClick = (e: React.MouseEvent) => {
  e.stopPropagation();
};

const onModalKeyDown = (e: React.KeyboardEvent) => {
  e.stopPropagation();
};

return (
  <>
    {/* 어노테이션 정보창 */}
    {annotationInfoOpen && selectedAnnotation && (
      <StAnnotationInfoPanel
        className="annotation-info-panel"
        onClick={(e) => e.stopPropagation()} // 패널 자체를 클릭했을 때도 맵이 닫히지 않도록 전파 방지
        style={{
          left: `${infoPanelPosition.x}px`,
          top: `${infoPanelPosition.y}px`,
          opacity: showEditModal ? 0.5 : 1,
          transition: 'opacity 0.2s'
        }}
      >
      <InfoHeader>
        <InfoTitle title={`${selectedAnnotation.content}`}>{selectedAnnotation.content}</InfoTitle>
        <CloseButton onClick={onCloseClick}>×</CloseButton>
      </InfoHeader>
      <InfoContent>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <div style={{
            width: '16px',
            height: '16px',
            backgroundColor: getCurrentColor(),
            borderRadius: '3px',
            border: '1px solid #ddd'
          }}></div>
          <InfoValue style={{ margin: 0 }}>{getShapeType(selectedAnnotation) || 'N/A'}</InfoValue>
        </div>
        <InfoValue>
          {selectedAnnotation.writer || selectedAnnotation.createdBy || 'Unknown'}{' '}
          {selectedAnnotation.createdAt
            ? new Date(selectedAnnotation.createdAt).toLocaleString('ko-KR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            : 'N/A'}
        </InfoValue>
        {!isReadOnly && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
            <Button
              icon={<EditOutlined />}
              onClick={onEditClick}
              size="small"
              style={{ flex: 1 }}
            >
              Edit
            </Button>
            <Button
              icon={<DeleteOutlined />}
              onClick={onDeleteClick}
              size="small"
              danger
              style={{ flex: 1 }}
            >
              Delete
            </Button>
          </div>
        )}
      </InfoContent>
    </StAnnotationInfoPanel>
    )}

    {/* 비밀번호 확인 모달 (수정/삭제 공용) */}
    {setPasswordModalOpen && setPasswordInput && handlePasswordConfirm && (
      <Modal
        centered
        title="비밀번호 확인"
        open={passwordModalOpen}
        onOk={handlePasswordConfirm}
        onCancel={() => { setPasswordModalOpen(false); setPasswordInput(""); }}
        okText="확인"
        cancelText="취소"
        zIndex={1002}
      >
        <Input.Password
          placeholder="등록 시 사용한 4자리 비밀번호를 입력하세요"
          value={passwordInput}
          onChange={(e) => setPasswordInput(e.target.value)}
          onPressEnter={handlePasswordConfirm}
          style={{ marginTop: "8px" }}
        />
      </Modal>
    )}

    {/* 어노테이션 수정 모달 */}
    {selectedAnnotation && (
      <div onClick={onModalClick} onKeyDown={onModalKeyDown}> {/* 모달 래퍼에도 전파 방지 적용 */}
        <Modal
          centered
          title="Edit Annotation"
          open={showEditModal}
          footer={null}
          onCancel={() => setShowEditModal(false)}
          zIndex={1002}
          destroyOnHidden
          maskClosable={false} // 모달 바깥 클릭 시 닫힘 방지 (OSD 클릭과 혼선 방지)
        >
          <Form
            form={annotationForm}
            onFinish={submitEditAnnotation}
            style={{ maxWidth: 600, marginTop: '1.5rem' }}
          >
            <Form.Item
              name="content"
              label="Description"
              style={{ margin: '0.6rem 0' }}
            >
              <TextArea rows={4} style={{ minHeight: '96px', maxHeight: '272px' }} />
            </Form.Item>
            <Form.Item
              name="color"
              label="Color"
              style={{ margin: '0.6rem 0 1rem 0' }}
            >
              <AnnotationColorBtn
                changeColor={changeColor}
                currentColor={getCurrentColor()}
              />
            </Form.Item>
            {requiresEditPassword && (
              <Form.Item
                name="anonymousPassword"
                label="Password"
                rules={[{ required: true, message: 'Password is required.' }]}
                style={{ margin: '0.6rem 0 1rem 0' }}
              >
                <Input.Password placeholder="Enter the password used when creating this annotation" />
              </Form.Item>
            )}
            <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Space>
                <Button type="primary" htmlType="submit">
                  Ok
                </Button>
                <Button
                  htmlType="button"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    )}
  </>
);
};

const StAnnotationInfoPanel = styled.div({
  position: 'absolute',
  width: '300px',
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  zIndex: 1001,
  overflow: 'hidden',
  pointerEvents: 'auto'
});

const InfoHeader = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #e0e0e0'
});

const InfoTitle = styled.h3({
  margin: 0,
  fontSize: '16px',
  fontWeight: 600,
  color: '#333',
  flex: 1,
  wordBreak: 'break-word',
  lineHeight: '1.4',
  marginRight: '8px',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
});

const CloseButton = styled.button({
  background: 'none',
  border: 'none',
  fontSize: '24px',
  color: '#666',
  cursor: 'pointer',
  padding: '0 4px',
  lineHeight: 1,
  '&:hover': {
    color: '#333'
  }
});

const InfoContent = styled.div({
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
});

const InfoValue = styled.div({
  color: '#333',
  fontSize: '14px',
  wordBreak: 'break-word',
  lineHeight: '1.5'
});
